/**
 * Turns a create-action error into a toast message, special-casing a
 * plan-limit 403 (`backend/app/core/plan_gating.py`'s `PlanGateError` —
 * documented invariant: its `detail` always contains the word "plan",
 * which is what distinguishes it from an RBAC `require_permission` 403,
 * whose detail is always exactly "Not permitted") with a friendlier,
 * trial-flavored message and an "Upgrade" action button.
 *
 * Used by Contacts/Workflows/Campaigns/Materials' create flows — see
 * docs/superpowers/specs/2026-09-03-free-trial-design.md addendum.
 */

export interface PlanLimitToastResult {
  message: string
  /** Whether the caller should attach an "Upgrade" action routing to Billing. */
  showUpgrade: boolean
}

export interface PlanLimitToastOptions {
  /** Whether the account is currently on the free trial (from useTrialStatus()). */
  isTrial: boolean
  /** Shown for any error that isn't a plan-limit 403 — today's existing generic message. */
  fallback: string
}

// Matches the exact shape assert_within_limit() builds:
// "Your plan's {label} limit ({limit}) has been reached. Upgrade your plan to add more."
const TRIAL_LIMIT_PATTERN = /plan's ([\w ]+) limit \((\d+)\)/i

export function planLimitToastMessage(error: unknown, { isTrial, fallback }: PlanLimitToastOptions): PlanLimitToastResult {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail

  if (typeof detail !== 'string' || !detail.toLowerCase().includes('plan')) {
    const messageFromError = typeof detail === 'string' ? detail : undefined
    return { message: messageFromError ?? fallback, showUpgrade: false }
  }

  if (!isTrial) {
    // A paid plan can hit a real cap too (e.g. Starter's 5,000 contacts) —
    // the backend's own message is already clear; just add the CTA.
    return { message: detail, showUpgrade: true }
  }

  const match = detail.match(TRIAL_LIMIT_PATTERN)
  if (!match) {
    return { message: detail, showUpgrade: true }
  }
  const [, label, limit] = match
  return {
    message: `You've used all ${limit} ${label} included in your free trial. Upgrade to keep going.`,
    showUpgrade: true,
  }
}
